import { Box, Flex, VStack, Heading, Text, IconButton } from "@chakra-ui/react";
import { AirportInput, AirportInputProps } from "./AirportInput";
import { GameActionCenter } from "./GameActionCenter/GameActionCenter";
import { Airport } from "@/models/airport";
import { PointToaster } from "../PointToaster/PointToaster";
import { descriptionForDistance } from "@/utils/airport_distance";
import { useEffect, useState } from "react";
import { t } from "i18next";
import { LuInfo } from "react-icons/lu";

interface ControlContainerProps {
  airline: string;
  duration: string;
  altitude: string;
  originTimezone: string;
  destinationTimezone: string;
  onRevealHint: (hint: string) => void;
  onGiveUp: () => void;
  onShowInfo: () => void;
  airports: Airport[];
  originInput: AirportInputProps;
  destinationInput: AirportInputProps;
}

function ControlContainer({
  airline,
  duration,
  altitude,
  originTimezone,
  destinationTimezone,
  onRevealHint,
  onGiveUp,
  onShowInfo,
  airports,
  originInput,
  destinationInput,
}: ControlContainerProps) {
  const [toasterVisible, setToasterVisible] = useState(false);
  const [toasterDescription, setToasterDescription] = useState("");
  const [toasterTriggerKey, setToasterTriggerKey] = useState(0);

  useEffect(() => {
    setToasterVisible(originInput.status === "error");
    setToasterDescription(descriptionForDistance(originInput.guessDistance));
    setToasterTriggerKey(toasterTriggerKey + 1);
  }, [originInput.status, originInput.value, originInput.guessDistance]);

  useEffect(() => {
    setToasterVisible(destinationInput.status === "error");
    setToasterDescription(
      descriptionForDistance(destinationInput.guessDistance)
    );
    setToasterTriggerKey(toasterTriggerKey + 1);
  }, [
    destinationInput.status,
    destinationInput.value,
    destinationInput.guessDistance,
  ]);

  return (
    <Box
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      width="100%"
      padding={0}
      pointerEvents="none"
      _before={{
        content: '""',
        position: "absolute",
        top: -20,
        right: 0,
        bottom: 0,
        left: 0,
        background:
          "linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0) 100%)",
        pointerEvents: "none",
      }}
      zIndex={10}
    >
      <Box position="relative" zIndex={1}>
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align="center"
          width="100%"
        >
          {/* Mobile */}
          <VStack width="100%" display={{ base: "flex", md: "none" }}>
            <PointToaster
              title={toasterDescription}
              visible={toasterVisible}
              triggerKey={toasterTriggerKey}
            />

            <AirportInput
              type="origin"
              onSubmitGuess={originInput.onSubmitGuess}
              airports={airports}
              zIndex={10000}
              value={originInput.value}
              status={originInput.status}
              guessDistance={originInput.guessDistance}
            />
            <AirportInput
              type="destination"
              onSubmitGuess={destinationInput.onSubmitGuess}
              airports={airports}
              zIndex={9999}
              value={destinationInput.value}
              status={destinationInput.status}
              guessDistance={destinationInput.guessDistance}
            />
            <Flex align="center" justify="center">
              <Heading fontSize="xl" color="white" mb={-2} fontWeight="600">
                {t("game.controls.needAHint")}
              </Heading>
              <IconButton
                aria-label="Info"
                onClick={onShowInfo}
                variant="ghost"
                color="white"
                pointerEvents="auto"
                size="sm"
                mt={2}
                _hover={{
                  backgroundColor: "transparent",
                }}
              >
                <LuInfo />
              </IconButton>
            </Flex>
            <Text color="white" fontFamily={"sans-serif"} fontSize="sm">
              {t("game.controls.revealKeyInfo")}
            </Text>
            <GameActionCenter
              airline={airline}
              altitude={altitude}
              duration={duration}
              originTimezone={originTimezone}
              destinationTimezone={destinationTimezone}
              onRevealHint={onRevealHint}
              onGiveUp={onGiveUp}
            />
          </VStack>

          {/* Desktop */}
          <Flex
            width="100%"
            justify="center"
            display={{ base: "none", md: "flex" }}
            gap={10}
            bottom={0}
            paddingBottom={{ base: 0, md: 30 }}
          >
            <VStack
              alignItems="flex-end"
              paddingLeft={{ base: 0, md: 10, lg: 0 }}
            >
              <Box width="100%" textAlign="center">
                <PointToaster
                  title={toasterDescription}
                  visible={toasterVisible}
                  triggerKey={toasterTriggerKey}
                />
                <Heading size={{ base: "xl", lg: "3xl" }} color="white">
                  {t("game.controls.guessTheFlight")}
                </Heading>
                <Text color="white" fontFamily={"sans-serif"}>
                  {t("game.controls.enterCityOrAirport")}
                </Text>
              </Box>
              <AirportInput
                type="origin"
                onSubmitGuess={originInput.onSubmitGuess}
                airports={airports}
                zIndex={10000}
                value={originInput.value}
                status={originInput.status}
                guessDistance={originInput.guessDistance}
              />
              <AirportInput
                type="destination"
                onSubmitGuess={destinationInput.onSubmitGuess}
                airports={airports}
                zIndex={9999}
                value={destinationInput.value}
                status={destinationInput.status}
                guessDistance={destinationInput.guessDistance}
              />
            </VStack>
            <VStack alignItems="flex-start">
              <Box width="100%" textAlign="center">
                <Flex align="center" justify="center">
                  <Heading size={{ base: "xl", lg: "3xl" }} color="white">
                    {t("game.controls.needAHint")}
                  </Heading>
                  <IconButton
                    aria-label="Info"
                    onClick={onShowInfo}
                    variant="ghost"
                    color="white"
                    pointerEvents="auto"
                    size="sm"
                    _hover={{
                      backgroundColor: "transparent",
                    }}
                  >
                    <LuInfo />
                  </IconButton>
                </Flex>
                <Text color="white" fontFamily={"sans-serif"}>
                  {t("game.controls.revealKeyInfo")}
                </Text>
              </Box>

              <GameActionCenter
                airline={airline}
                altitude={altitude}
                duration={duration}
                originTimezone={originTimezone}
                destinationTimezone={destinationTimezone}
                onRevealHint={onRevealHint}
                onGiveUp={onGiveUp}
              />
            </VStack>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
}

export default ControlContainer;
